import React, { useState, useEffect, useCallback } from 'react';
import { stageAPI } from '../services/api';

/**
 * SUIVI DE STAGE — Certification › Paramétrage › Attestations (lot C.0 bis)
 * ==========================================================================
 * Spec     : doc/modules/suivi_de_stage_certification.md § 2 c)
 * Maquette : #cpc-attestations — le texte d'introduction de la page du maître et
 * les libellés des trois attestations.
 *
 * S'ajoute à la maquette (décision CC5, la spec l'emporte) : le texte de
 * l'AVERTISSEMENT de la déclaration hors entreprise, qui est la preuve même de
 * la déclaration (il est recopié tel quel au moment où l'étudiant déclare).
 *
 * ⚠️ Pas d'historique ni de « Rétablir » (décision du 2026-10-05) : chaque
 * processus lancé garde ses textes dans sa copie figée (C18). Seul « modifié par
 * … le … » est tenu.
 *
 * ⚠️ Le brouillon ne se réinitialise que lorsque le SERVEUR a répondu (valeurs
 * enregistrées), jamais à chaque rendu du parent : la saisie en cours survit.
 */

const CHAMPS = [
    { champ: 'libelle_lecture',         libelle: 'Lecture' },
    { champ: 'libelle_periode',         libelle: 'Période' },
    { champ: 'libelle_confidentialite', libelle: 'Confidentialité' }
];

const TOUS = ['texte_intro', ...CHAMPS.map((c) => c.champ), 'avertissement'];

function StageCertifAttestations({ donnees, departementId, modifiable, onMiseAJour, processusId = null }) {
    const reglages = donnees.reglages || {};
    const [brouillon, setBrouillon] = useState(() => Object.fromEntries(TOUS.map((k) => [k, reglages[k] || ''])));
    const [enCours, setEnCours]     = useState(false);
    const [erreur, setErreur]       = useState(null);
    const [enregistre, setEnregistre] = useState(false);

    const referenceServeur = JSON.stringify(TOUS.map((k) => reglages[k]));
    useEffect(() => {
        setBrouillon(Object.fromEntries(TOUS.map((k) => [k, reglages[k] || ''])));
    }, [referenceServeur]); // eslint-disable-line react-hooks/exhaustive-deps

    const modifie = TOUS.some((k) => (brouillon[k] ?? '') !== (reglages[k] ?? ''));
    const poser = (k, v) => setBrouillon((b) => ({ ...b, [k]: v }));

    const enregistrer = useCallback(async () => {
        setEnCours(true);
        setErreur(null);
        try {
            // L2b (E170) : dans une vignette, les textes de CE processus.
            const r = await stageAPI.enregistrerReglagesCertif(departementId, brouillon, processusId);
            onMiseAJour({ reglages: r.data.reglages });
            setEnregistre(true);
            setTimeout(() => setEnregistre(false), 2500);
        } catch (e) {
            const d = e.response?.data;
            setErreur(d?.motifs?.length ? d.motifs.join(' · ') : (d?.error || "L'enregistrement a échoué"));
        } finally {
            setEnCours(false);
        }
    }, [departementId, brouillon, onMiseAJour, processusId]);

    return (
        <div className="card" style={{ padding: 16 }}>
            {erreur && (
                <div className="stg-encart stg-encart-erreur">
                    <i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span>
                </div>
            )}

            <div className="form-group" style={{ marginBottom: 12 }}>
                <label htmlFor="ca-intro" style={{ fontSize: 11, fontWeight: 600, color: 'var(--stg-muted)' }}>
                    Texte d'introduction <span style={{ fontWeight: 400 }}>(en haut de la page du maître)</span> <span className="form-required">*</span>
                </label>
                <textarea id="ca-intro" className="input-sm" style={{ minHeight: 70, width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                          maxLength={2000} disabled={!modifiable}
                          value={brouillon.texte_intro} onChange={(e) => poser('texte_intro', e.target.value)} />
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--stg-muted)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                Libellés des attestations
            </div>
            {CHAMPS.map((c) => (
                <div className="cfg-param-row" key={c.champ}>
                    <label htmlFor={'ca-' + c.champ}>{c.libelle} <span className="form-required">*</span></label>
                    <input id={'ca-' + c.champ} className="input-sm" style={{ flex: 1 }} maxLength={300} disabled={!modifiable}
                           value={brouillon[c.champ]} onChange={(e) => poser(c.champ, e.target.value)} />
                </div>
            ))}

            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--stg-muted)', textTransform: 'uppercase', letterSpacing: '.5px', margin: '14px 0 8px' }}>
                Avertissement de la déclaration hors entreprise
            </div>
            <div className="form-group" style={{ marginBottom: 6 }}>
                <label htmlFor="ca-avert" style={{ fontSize: 11, fontWeight: 600, color: 'var(--stg-muted)' }}>
                    Affiché en rouge, bien visible, sur la page de l'étudiant <span className="form-required">*</span>
                </label>
                <textarea id="ca-avert" className="input-sm" style={{ minHeight: 50, width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                          maxLength={1000} disabled={!modifiable}
                          value={brouillon.avertissement} onChange={(e) => poser('avertissement', e.target.value)} />
            </div>
            <div style={{ fontSize: 10, color: 'var(--stg-muted)' }}>
                <i className="fa-solid fa-circle-info" /> Le texte exact affiché est recopié avec chaque déclaration : c'est la preuve que l'étudiant l'avait sous les yeux.
            </div>

            <div style={{ fontSize: 10, color: 'var(--stg-muted)', marginTop: 10 }}>
                <i className="fa-solid fa-lock" /> Les dossiers déjà lancés gardent les textes de leur premier envoi.
            </div>

            <div className="certif-pied">
                <button type="button" className="btn btn-green btn-sm" disabled={!modifiable || enCours || !modifie} onClick={enregistrer}>
                    {enCours ? <><i className="fa-solid fa-circle-notch stg-tourne" /> …</> : <><i className="fa-solid fa-check" /> Enregistrer</>}
                </button>
                <span style={{ fontSize: 11, color: 'var(--stg-muted)' }}>
                    {modifie ? <><i className="fa-solid fa-pen" /> Modifications non enregistrées</> : null}
                </span>
                {enregistre && <span style={{ fontSize: 11, color: '#15803d', fontWeight: 600 }}><i className="fa-solid fa-check" /> Enregistré</span>}
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--stg-muted)' }}>
                    {reglages.updated_par_nom
                        ? `Modifié par ${reglages.updated_par_nom} le ${new Date(reglages.updated_at).toLocaleDateString('fr-FR')}`
                        : "Textes d'origine"}
                </span>
            </div>
            {!modifiable && (
                <div style={{ fontSize: 11, color: 'var(--stg-muted)', marginTop: 6 }}>
                    <i className="fa-solid fa-circle-info" /> Lecture seule : votre profil ne permet pas de modifier ce module.
                </div>
            )}
        </div>
    );
}

export default StageCertifAttestations;
